// Helpers del PORTAL CIUDADANO (Módulo Disciplinario).
//
// Dos grupos de funciones:
//  - PÚBLICAS (sin token): radicar queja, consultar estado y aportar documento.
//    El aporte de documento hace un fetch multipart PROPIO, SIN Authorization
//    (no usa el helper subir(), que adjunta el token del funcionario).
//  - AUTENTICADAS (con login, roles DISCIPLINADO/DEFENSOR): mis actuaciones y
//    la presentación de descargos, pruebas, nulidades, recursos y poder; usan
//    api()/subir() que adjuntan el token.

import { api, subir } from './api';

const API = process.env.NEXT_PUBLIC_API_URL ?? 'http://10.10.30.130:4601/api/v1';

// ===========================================================================
// Tipos
// ===========================================================================

export interface QuejosoInput {
  nombre?: string;
  correo: string;
  telefono?: string;
}

export interface RadicarQuejaInput {
  descripcionHechos: string;
  fechaHechos?: string;
  lugar?: string;
  quejoso?: QuejosoInput;
  investigadoNombre?: string;
  entidadNombre?: string;
  anonimo?: boolean;
  captchaToken?: string;
}

export interface RadicarQuejaResp {
  radicado: string;
  estadoActual: string;
  mensaje: string;
}

export interface ActuacionPublica {
  tipo: string;
  fecha: string;
}

export interface ConsultaEstadoResp {
  radicado: string;
  estadoGeneral: string;
  etapa: string;
  ultimaActuacionPublica: ActuacionPublica | null;
}

export interface DocumentoAportadoResp {
  path: string;
  filename: string;
  url: string;
}

export interface MiActuacion {
  expedienteId: string;
  radicado: string;
  tipo: string;
  fecha: string;
  documentoUrl: string;
}

// ===========================================================================
// PÚBLICAS (sin token)
// ===========================================================================

/** Radica una queja ciudadana (POST /portal/quejas). Devuelve el radicado. */
export function radicarQueja(body: RadicarQuejaInput): Promise<RadicarQuejaResp> {
  return api<RadicarQuejaResp>('/portal/quejas', {
    method: 'POST',
    auth: false,
    body,
  });
}

/** Consulta el estado público de un expediente (GET /portal/consulta). */
export function consultarEstado(
  radicado: string,
  correo: string,
): Promise<ConsultaEstadoResp> {
  const qs = new URLSearchParams({ radicado, correo }).toString();
  return api<ConsultaEstadoResp>(`/portal/consulta?${qs}`, { auth: false });
}

/**
 * Aporta un documento a un expediente por el portal público (multipart).
 * Hace un fetch PROPIO sin Authorization (endpoint público). Verifica el correo.
 */
export async function aportarDocumentoPublico(
  radicado: string,
  file: File,
  correo: string,
): Promise<DocumentoAportadoResp> {
  const form = new FormData();
  form.append('file', file);
  form.append('correo', correo);

  const res = await fetch(
    `${API}/portal/consulta/${encodeURIComponent(radicado)}/documentos`,
    { method: 'POST', body: form },
  );
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const m = data?.message;
    throw new Error(Array.isArray(m) ? m.join(' · ') : (m ?? `Error ${res.status}`));
  }
  return data as DocumentoAportadoResp;
}

// ===========================================================================
// AUTENTICADAS (con token · roles DISCIPLINADO / DEFENSOR)
// ===========================================================================

/** Lista las actuaciones de la parte autenticada (GET /portal/mis-actuaciones). */
export function misActuaciones(): Promise<MiActuacion[]> {
  return api<MiActuacion[]>('/portal/mis-actuaciones');
}

/** Presenta descargos (POST /portal/mis-actuaciones/descargos, multipart). */
export function enviarDescargos<T = unknown>(fd: FormData): Promise<T> {
  return subir<T>('/portal/mis-actuaciones/descargos', fd);
}

/** Aporta una prueba (POST /portal/mis-actuaciones/pruebas, multipart). */
export function enviarPrueba<T = unknown>(fd: FormData): Promise<T> {
  return subir<T>('/portal/mis-actuaciones/pruebas', fd);
}

/** Solicita nulidad (POST /portal/mis-actuaciones/nulidades, multipart). */
export function enviarNulidad<T = unknown>(fd: FormData): Promise<T> {
  return subir<T>('/portal/mis-actuaciones/nulidades', fd);
}

/** Interpone recurso (POST /portal/mis-actuaciones/recursos, multipart). */
export function enviarRecurso<T = unknown>(fd: FormData): Promise<T> {
  return subir<T>('/portal/mis-actuaciones/recursos', fd);
}

/** Gestiona poder (POST /portal/apoderado/poder, multipart · solo DEFENSOR). */
export function gestionarPoder<T = unknown>(fd: FormData): Promise<T> {
  return subir<T>('/portal/apoderado/poder', fd);
}
